"use client";

import { Dialog } from "@base-ui/react/dialog";
import { NavigationMenu } from "@base-ui/react/navigation-menu";
import { ChevronDown, Menu, X } from "lucide-react";
import { type CSSProperties, useId } from "react";

import { SOLUTION_SECTORS, solutionHref } from "@/app/solutions/sectors";
import { docsHref } from "@/lib/docs-links";
import {
  BODY_MUTED,
  FeatureCard,
  FOCUS_INSET_CLS,
  LinkArrow,
  type NavGroupId,
  QUIET,
} from "./nav-feature-cards";
import { ACCOUNTANTS, CONSULTANCIES } from "./sectors/content";
import { PARTNER_HREF } from "./site-links";
import {
  DISPLAY,
  EDITORIAL_ACTION_CLS,
  GROUND,
  INK,
  LABEL_CLS,
  mix,
  SURFACE_DARK,
} from "./theme";
import type { Cta } from "./types";

export type { NavGroupId } from "./nav-feature-cards";

/* ================================================================= */
/*  Structure: four groups; the CTA sits beside them                   */
/* ================================================================= */

type NavItem = { label: string; href: string; external?: boolean; line?: string };
type NavGroup = { id: NavGroupId; label: string; items: NavItem[] };

/** Sector titles are two display lines; the menu reads them as one. */
const sectorLine = (title: string) => title.replace(/\n/g, " ");

const NAV_GROUPS: NavGroup[] = [
  {
    id: "product",
    label: "Product",
    items: [
      { label: "How it works", href: "/#platform" },
      { label: "Pricing", href: "/pricing" },
      { label: "Changelog", href: "/changelog" },
      { label: "Docs", href: docsHref(), external: true },
    ],
  },
  {
    id: "solutions",
    label: "Solutions",
    items: [
      { label: "Accountancy firms", href: "/for/accountants", line: sectorLine(ACCOUNTANTS.title) },
      { label: "Agencies & consultancies", href: "/for/consultancies", line: sectorLine(CONSULTANCIES.title) },
      ...SOLUTION_SECTORS.map((sector) => ({
        label: sector.name,
        href: solutionHref(sector),
        line: sector.hero.title.join(" "),
      })),
    ],
  },
  {
    id: "resources",
    label: "Resources",
    items: [
      { label: "Blog", href: "/blog" },
      { label: "Research", href: "/research" },
      { label: "Case studies", href: "/case-studies" },
    ],
  },
  {
    id: "company",
    label: "Company",
    items: [
      { label: "About", href: "/about" },
      { label: "Become a partner", href: PARTNER_HREF, external: true },
    ],
  },
];

/** A panel list this long runs in two columns of names, without their
 *  lines, so the panel stays short; the phone sheet keeps the lines. */
const TWO_COLUMN_FROM = 7;

/* ================================================================= */
/*  Tokens                                                             */
/* ================================================================= */

const RULE = mix("--theme-ink", 14);
const CARD_GROUND = mix("--theme-ink", 4);

/** Focus ring for controls on the bar, in the bar's text colour (`--nav-fg`,
 *  set by EditorialNav), so it shows on the cream, clear and dark bars. */
export const BAR_FOCUS_CLS =
  "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nav-fg)]";
/** Focus ring for controls on the cream panels and sheet. */
const FOCUS_CLS =
  "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-[color:var(--theme-ink)]";

const ARROW_TONE_CLS =
  "text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)] group-hover/link:text-[var(--theme-accent-text)]";

const MOTION_VARS = {
  "--duration": "0.3s",
  "--easing": "cubic-bezier(0.22, 1, 0.36, 1)",
} as CSSProperties;

/** Content swaps slide a short way in from the side the pointer came from. */
const CONTENT_CLS = [
  "h-full w-[min(44rem,calc(100vw-2rem))]",
  "transition-[opacity,translate] duration-[var(--duration)] ease-[var(--easing)] motion-reduce:transition-none",
  "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
  "data-[starting-style]:data-[activation-direction=left]:-translate-x-6",
  "data-[starting-style]:data-[activation-direction=right]:translate-x-6",
  "data-[ending-style]:data-[activation-direction=left]:translate-x-6",
  "data-[ending-style]:data-[activation-direction=right]:-translate-x-6",
].join(" ");

/* ================================================================= */
/*  Shared bits                                                        */
/* ================================================================= */

/* ================================================================= */
/*  Desktop: Base UI Navigation Menu                                   */
/* ================================================================= */

function PanelLinks({ group }: { group: NavGroup }) {
  const columns = group.items.length >= TWO_COLUMN_FROM;
  return (
    <ul className={columns ? "grid grid-cols-2 gap-x-6 border-t" : "border-t"} style={{ borderColor: RULE }}>
      {group.items.map((item) => (
        <li key={item.href}>
          <NavigationMenu.Link
            href={item.href}
            closeOnClick
            className={`group/link flex min-h-12 items-center justify-between gap-4 border-b py-3 ${columns ? "h-full " : ""}${FOCUS_INSET_CLS}`}
            style={{ borderColor: RULE, color: INK }}
          >
            <span className="min-w-0">
              <span className="block text-[1rem] leading-[1.3] font-medium">{item.label}</span>
              {item.line && !columns ? (
                <span className="mt-1 block text-[0.86rem] leading-[1.45]" style={{ color: BODY_MUTED }}>
                  {item.line}
                </span>
              ) : null}
            </span>
            <span className={ARROW_TONE_CLS}>
              <LinkArrow external={item.external} />
            </span>
          </NavigationMenu.Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * The four menus. Every panel opens from `anchor` (the bar's right-hand
 * cluster), flush under the bar's hairline and aligned to the CTA's right
 * edge, so only the panel's height changes between groups. Panels are always
 * cream; the triggers take the bar's text colour.
 */
export function NavDesktopMenu({
  anchor,
  color,
  className,
  onOpenChange,
}: {
  anchor: HTMLElement | null;
  color: string;
  className: string;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <NavigationMenu.Root
      aria-label="Site navigation"
      className={`h-full items-center ${className}`}
      onValueChange={(value) => onOpenChange?.(value !== null)}
    >
      <NavigationMenu.List className="flex h-full items-center gap-5 xl:gap-7">
        {NAV_GROUPS.map((group) => (
          <NavigationMenu.Item key={group.id} value={group.id} className="flex h-full items-center">
            <NavigationMenu.Trigger
              className={`group/trigger inline-flex min-h-11 items-center gap-1 text-[0.92rem] font-medium transition-opacity hover:opacity-70 data-[popup-open]:opacity-100 motion-reduce:transition-none ${BAR_FOCUS_CLS}`}
              style={{ color }}
            >
              <span className="decoration-1 underline-offset-[6px] group-data-[popup-open]/trigger:underline">
                {group.label}
              </span>
              <NavigationMenu.Icon className="inline-flex transition-transform duration-200 ease-out data-[popup-open]:rotate-180 motion-reduce:transition-none">
                <ChevronDown aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
              </NavigationMenu.Icon>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className={CONTENT_CLS}>
              <div className="grid grid-cols-[minmax(0,1fr)_18rem]">
                <div className="px-7 pt-6 pb-7">
                  <p className={LABEL_CLS} style={{ color: QUIET }}>
                    {group.label}
                  </p>
                  <div className="mt-4">
                    <PanelLinks group={group} />
                  </div>
                </div>
                <div className="border-l" style={{ borderColor: RULE, backgroundColor: CARD_GROUND }}>
                  <FeatureCard group={group.id} compact={false} inMenu />
                </div>
              </div>
            </NavigationMenu.Content>
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>

      <NavigationMenu.Portal>
        <NavigationMenu.Positioner
          anchor={anchor ?? undefined}
          side="bottom"
          align="end"
          sideOffset={1}
          collisionPadding={16}
          collisionAvoidance={{ side: "none" }}
          className="z-[65] h-[var(--positioner-height)] w-[var(--positioner-width)] max-w-[var(--available-width)]"
          style={MOTION_VARS}
        >
          <NavigationMenu.Popup
            className="relative h-[var(--popup-height)] w-[var(--popup-width)] border border-t-0 outline-none transition-[opacity,translate,height] duration-[var(--duration)] ease-[var(--easing)] data-[starting-style]:-translate-y-1 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 data-[ending-style]:duration-150 motion-reduce:transition-none"
            style={{
              ...DISPLAY,
              backgroundColor: GROUND,
              color: INK,
              borderColor: RULE,
              boxShadow: `0 28px 48px -36px ${mix("--theme-ink", 55)}`,
            }}
          >
            <NavigationMenu.Viewport className="relative h-full w-full overflow-hidden" />
          </NavigationMenu.Popup>
        </NavigationMenu.Positioner>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
}

/* ================================================================= */
/*  Mobile: full-height sheet with native accordions                   */
/* ================================================================= */

/**
 * The phone menu: a modal sheet from the right (focus trapped, page scroll
 * locked, Escape and the backdrop close it and focus returns to the toggle),
 * one accordion per group with its feature card, and the CTA pinned at the
 * foot. `contained` draws it inside a review frame on the concept board
 * instead of over the viewport, without trapping the board's focus.
 */
export function NavMobileSheet({
  cta,
  open,
  onOpenChange,
  color,
  triggerClassName,
  contained = false,
  container = null,
  defaultGroup = null,
}: {
  cta: Cta;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  color: string;
  triggerClassName: string;
  contained?: boolean;
  container?: HTMLElement | null;
  defaultGroup?: NavGroupId | null;
}) {
  const groupName = useId();
  const position = contained ? "absolute" : "fixed";
  const close = () => onOpenChange(false);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => onOpenChange(next)}
      modal={!contained}
      disablePointerDismissal={contained}
    >
      <Dialog.Trigger
        aria-label="Open navigation"
        className={`inline-flex size-11 items-center justify-center ${BAR_FOCUS_CLS} ${triggerClassName}`}
        style={{ color }}
      >
        <Menu aria-hidden="true" strokeWidth={1.5} />
      </Dialog.Trigger>
      <Dialog.Portal container={contained ? container : undefined}>
        <Dialog.Backdrop
          onClick={contained ? close : undefined}
          className={`${position} inset-0 z-[70] transition-opacity duration-200 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 motion-reduce:transition-none`}
          style={{ backgroundColor: mix("--theme-ink", 45) }}
        />
        <Dialog.Popup
          aria-label="Site navigation"
          initialFocus={contained ? false : undefined}
          className={`${position} inset-y-0 right-0 z-[71] flex w-[calc(100%-3rem)] max-w-[26rem] flex-col outline-none transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full motion-reduce:transition-none`}
          style={{ ...DISPLAY, backgroundColor: GROUND, color: INK, borderLeft: `1px solid ${RULE}` }}
        >
          <div
            className="flex min-h-17 shrink-0 items-center justify-between border-b pr-3 pl-5"
            style={{ borderColor: RULE }}
          >
            <span className={LABEL_CLS} style={{ color: QUIET }}>
              Menu
            </span>
            <Dialog.Close
              aria-label="Close navigation"
              className={`inline-flex size-11 items-center justify-center ${FOCUS_CLS}`}
              style={{ color: INK }}
            >
              <X aria-hidden="true" strokeWidth={1.5} />
            </Dialog.Close>
          </div>

          <nav
            aria-label="Site navigation"
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5"
          >
            {NAV_GROUPS.map((group) => (
              <details
                key={group.id}
                name={groupName}
                open={group.id === defaultGroup}
                className="group/acc border-b"
                style={{ borderColor: RULE }}
              >
                <summary
                  className={`flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 text-[1.05rem] font-semibold tracking-[-0.01em] [&::-webkit-details-marker]:hidden ${FOCUS_INSET_CLS}`}
                >
                  {group.label}
                  <ChevronDown
                    aria-hidden="true"
                    className="size-4 transition-transform duration-200 ease-out group-open/acc:rotate-180 motion-reduce:transition-none"
                    strokeWidth={1.75}
                  />
                </summary>
                <div className="pb-5">
                  <ul>
                    {group.items.map((item) => (
                      <li key={item.href}>
                        <a
                          href={item.href}
                          onClick={close}
                          className={`group/link flex min-h-11 items-center justify-between gap-4 border-t py-2.5 ${FOCUS_INSET_CLS}`}
                          style={{ borderColor: RULE, color: INK }}
                        >
                          <span className="min-w-0">
                            <span className="block text-[0.98rem] leading-[1.3] font-medium">{item.label}</span>
                            {item.line ? (
                              <span className="mt-0.5 block text-[0.84rem] leading-[1.45]" style={{ color: BODY_MUTED }}>
                                {item.line}
                              </span>
                            ) : null}
                          </span>
                          <span className={ARROW_TONE_CLS}>
                            <LinkArrow external={item.external} />
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2" style={{ backgroundColor: CARD_GROUND }}>
                    <FeatureCard group={group.id} compact inMenu={false} onNavigate={close} />
                  </div>
                </div>
              </details>
            ))}
          </nav>

          <div
            className="shrink-0 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
            style={{ borderColor: RULE }}
          >
            <a
              href={cta.href}
              onClick={close}
              className={`${EDITORIAL_ACTION_CLS} w-full ${FOCUS_CLS}`}
              style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
            >
              {cta.label}
            </a>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
